import React, { useState, useCallback, useEffect } from "react";
import * as Mui from "@mui/material";
import * as Icons from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  incentiveType,
  payoutBasis,
  payoutType,
  qualificationCondition,
} from "pages/HR/Headers";
import { tooltipStyleProps } from "services/constants/Styles";
import { Cancel, AddCircle, DoubleArrow } from "@material-ui/icons";
import { EligibilityType } from "pages/HR/Headers";
import { subHeaderStyle } from "services/constants";
import sessionHandling from "services/utils/notificationUtils";
import {
  threeDigitTwoDecimalPattern,
  fourDigitTwoDecimalPattern,
  percentagePattern,
} from "services/constants/patterns";
import { defaultAllSelection } from "services/constants";

export const Incentive = ({
  onClose,
  reCallApi,
  selectedIncentiveData,
  isEdit = false,
  setHandleLoader,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      name: "",
      incentive_type: "",
      qualification_type: "",
      qualification_value: "",
      payout_type: "",
      payout_value: "",
      payout_basis: "",
      maximum_cap: "",
      is_maximum_cap_allowed: false,
      eligibility_logic: "AND",
      salary_divisor: 0,
    },
  });
  const { domain, token, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const { result: departmentData } = useFetch(`${domain}department/`);
  const { result: designationData } = useFetch(`${domain}designation/`);
  const { result: roleData } = useFetch(
    `${domain}product-control/roles/summary/`,
  );
  const { result: individualData } = useFetch(
    `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${defaultAllSelection}&status=Active`,
  );
  const [rows, setRows] = useState([{ eligiblity_type: "", departments: "" }]);
  const [rowError, setRowError] = useState([
    { eligiblity_type: false, departments: false },
  ]);
  const [trigger, setTrigger] = useState(true);
  const existingName = selectedIncentiveData?.name;

  const handleIncentiveName = (event) => {
    setValue("name", event.target.value);
  };

  const handleIncentiveType = (_, data) => {
    setValue("incentive_type", data?.label ?? "");
  };

  const departmentList =
    departmentData?.map(({ id, department }) => ({
      id,
      name: department,
    })) ?? [];
  const designationList =
    designationData?.map(({ id, designation }) => ({
      id,
      name: designation,
    })) ?? [];

  const roleList =
    roleData?.map(({ role_id, role_name }) => ({
      id: role_id,
      name: role_name,
    })) ?? [];

  const individualList =
    individualData?.map(({ user_id, username }) => ({
      id: user_id,
      name: username,
    })) ?? [];

  const onSubmit = async (data) => {
    let isValid = await validateRows();
    if (isValid) {
      setValue("button_loader", true);
      const incentive_eligibilities = [];

      rows.forEach((item) => {
        const ids = (item.departments || []).map((d) => d.id);

        if (ids.length > 0) {
          incentive_eligibilities.push({
            eligibility_type: item.eligiblity_type,
            eligibility_id: ids,
          });
        }
      });
      let payload = {
        name: data?.name,
        incentive_type: data?.incentive_type,
        qualification_type: data?.qualification_type,
        qualification_value: data?.qualification_value,
        payout_type:
          data?.payout_type === "% of salary"
            ? "Percentage of salary"
            : data?.payout_type,
        payout_value: data?.payout_value,
        payout_basis: data?.payout_basis,
        eligibility_logic: data?.eligibility_logic,
        is_maximum_cap_allowed: data?.is_maximum_cap_allowed,
        maximum_cap: data?.maximum_cap,
        incentive_eligibilities: incentive_eligibilities,
        salary_divisor: data?.salary_divisor,
      };
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit
        ? `${domain}hrzone/incentive/incentive-template/${selectedIncentiveData?.id}/`
        : `${domain}hrzone/incentive/incentive-template/`;
      const response = await customAPI(url, method, payload);
      if (response.ok) {
        showToast(
          "success",
          isEdit
            ? "Incentive template updated successfully"
            : "Incentive template created successfully",
        );
        reCallApi(true);
        onClose();
        setValue("button_loader", false);
        if (isEdit) {
          setHandleLoader(true);
        }
      } else {
        setValue("button_loader", false);
      }
    }
    setValue("button_loader", false);
  };

  const handleQualification = (_, data) => {
    setValue("qualification_type", data?.label ?? "");
    setValue("qualification_value", "");
  };

  const handlePayoutType = (_, data) => {
    setValue("payout_type", data?.label ?? "");
    setValue("payout_value", "");
    setValue("payout_basis", "");
  };

  const handlePayoutDivisor = (event) => {
    if (/^(?:[1-9]|[12][0-9]|3[01])$/.test(event.target.value) || event.target.value==="") {
      setValue("salary_divisor", event.target.value);
    }
  };

  const hanldePayoutBasis = (_, data) => {
    setValue("payout_basis", data?.label ?? "");
  };

  const handleQualificationPercentage = (event) => {
    if (
      percentagePattern.test(event.target.value) ||
      event.target.value === ""
    ) {
      setValue("qualification_value", event.target.value ?? "");
    }
  };

  const handleQualificationCount = (event) => {
    if (
      threeDigitTwoDecimalPattern.test(event.target.value) ||
      event.target.value === ""
    ) {
      setValue("qualification_value", event.target.value ?? "");
    }
  };

  const handlePayOutValue = (event) => {
    if (
      watch("payout_type") === "% of salary"
        ? percentagePattern.test(event.target.value)
        : fourDigitTwoDecimalPattern.test(event.target.value) ||
          event.target.value === ""
    ) {
      setValue("payout_value", event.target.value ?? "");
    }
  };

  const handleMaximumcap = (event) => {
    if (
      fourDigitTwoDecimalPattern.test(event.target.value) ||
      event.target.value === ""
    ) {
      setValue("maximum_cap", event.target.value ?? "");
    }
  };

  const validateRows = useCallback(() => {
    const newErrors = rows.map((row, index) => {
      const minEmpty = row.eligiblity_type === "";
      const compEmpty = row.departments?.length === 0;
      return {
        eligiblity_type: minEmpty,
        departments: compEmpty,
      };
    });

    const hasError = newErrors.some(
      (err) => err.eligiblity_type || err.departments,
    );

    setRowError(newErrors);
    return !hasError;
  }, [rows]);

  const handleAddRow = () => {
    const isValid = validateRows();
    if (!isValid) return;

    setRows((prev) => [...prev, { eligiblity_type: "", departments: "" }]);
    setRowError((prev) => [
      ...prev,
      { eligiblity_type: false, departments: false },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowError((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
    const updatedErrors = [...rowError];
    if (!updatedErrors[index])
      updatedErrors[index] = { eligiblity_type: false, departments: false };
    updatedErrors[index][field] = false;
    setRowError(updatedErrors);
  };

  useEffect(() => {
    if (isEdit && selectedIncentiveData && trigger) {
    reset({
      ...selectedIncentiveData,
      payout_type: selectedIncentiveData?.payout_type_display,
    });
      const summary = selectedIncentiveData.eligibility_summary;
      const formattedRows = Object.keys(summary).map((key) => ({
        eligiblity_type: key,
        departments: summary[key],
      }));
      setRows(formattedRows);
      setTrigger(false);
    }
  }, [isEdit, selectedIncentiveData, reset, trigger]);

  const labelStyle = {
    fontSize: "16px",
    variant: "body2",
    color: "text.secondary",
  };

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Incentive Template
            </Typography>
            <Typography {...infoStyle}>
              Define how this incentive works, who is eligible, and how it will
              be calculated and paid.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={{ ...childBoxStyle, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Incentive Template Name
              </Typography>
              <Typography {...infoStyle}>
                A unique name to identify this incentive rule.
              </Typography>
              <CustomTextBox
                selectedValue={watch("name")}
                rules={{
                  ...register("name", {
                    required: "Incentive template name is required",
                    validate: {
                      checkIncentiveName: async (fieldValue) => {
                        const value = fieldValue.trim();
                        if (value === "") {
                          return "Incentive template name is required";
                        }

                        if (
                          existingName &&
                          existingName.toLowerCase() === value.toLowerCase()
                        ) {
                          return true;
                        }

                        try {
                          const response = await fetch(
                            `${domain}hrzone/incentive/unique-incentive-template/?name=${value}`,
                            {
                              method: "GET",
                              headers: {
                                "content-type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );

                          const res = await response.json();

                          if (response.ok) {
                            if (res.exists) {
                              return "Incentive template name already exists";
                            }
                            return true;
                          } else if (response.status === 409) {
                            sessionHandling();
                            return "Session expired";
                          } else {
                            throw new Error('REQUEST_FAILED');
                          }
                        } catch (error) {
                          console.error('[Incentive] operation failed:', error);
                          showToast("error", "Couldn't save the incentive details. Please try again.");
                          return "Validation failed";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handleIncentiveName}
                placeHolder="Enter the incentive name"
                errorMessage={
                  errors?.name?.message ? errors?.name?.message : ""
                }
                textBoxError={errors.name ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Incentive Type</Typography>
              <Typography {...infoStyle}>
                Choose what behavior or performance this incentive is based on.
              </Typography>
              <CustomAutoComplete
                dataList={incentiveType}
                selectedValue={watch("incentive_type")}
                rules={{
                  ...register("incentive_type", {
                    required: "Incentive type is required",
                  }),
                }}
                updateDataFunction={handleIncentiveType}
                placeHolder="Select the incentive type"
                errorMessage={
                  errors?.incentive_type?.message
                    ? errors?.incentive_type?.message
                    : ""
                }
                textBoxError={errors?.incentive_type ? true : false}
              />
            </Box>
            <Divider />
            <Box>
              <Typography sx={subHeaderStyle}>Eligibility Logic</Typography>
            </Box>
            <Typography sx={{ fontWeight: 600 }}>Eligibility Logic</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Controller
                name="eligibility_logic"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    row
                    {...field}
                    sx={{ gap: "2px", alignItems: "center" }}
                  >
                    <FormControlLabel
                      value="AND"
                      control={
                        <Radio
                          sx={{
                            color: "&.Mui-checked"
                              ? themes?.stepActiveColor
                              : "inherit",
                          }}
                        />
                      }
                      label={
                        <Typography sx={labelStyle}>
                          Match ALL conditions (AND)
                        </Typography>
                      }
                    />

                    <FormControlLabel
                      value="OR"
                      control={<Radio />}
                      label={
                        <Typography sx={labelStyle}>
                          Match ANY condition (OR)
                        </Typography>
                      }
                    />
                  </RadioGroup>
                )}
              />
            </Box>
            <Divider />
            <Box>
              <Typography sx={subHeaderStyle}>
                {" "}
                Eligibility Selection
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Eligibility Selection
              </Typography>
              <Typography {...infoStyle}>
                Select which employees are allowed to receive this incentive.
              </Typography>

              <Mui.TableContainer
                sx={{
                  width: "100%",
                  mt: 1,
                }}
              >
                <Mui.Table className="border">
                  {rows.map((row, index) => {
                    const selectedDepartments = rows
                      .filter((_, i) => i !== index)
                      .map((r) => r.eligiblity_type)
                      .filter(Boolean);

                    return (
                      <Mui.TableBody key={index}>
                        <Mui.TableCell sx={{ width: "50%" }}>
                          <Mui.Autocomplete
                            disablePortal
                            id={`min-percentage-${index}`}
                            size="small"
                            value={row.eligiblity_type}
                            options={EligibilityType?.map((obj) => ({
                              ...obj,
                              label: obj?.name,
                            }))}
                            getOptionDisabled={(option) =>
                              selectedDepartments.includes(option.name)
                            }
                            renderOption={(props, option) => {
                              return <li {...props}>{option.label}</li>;
                            }}
                            filterOptions={(options, state) =>
                              options?.filter((option) =>
                                option.label
                                  ?.toLowerCase()
                                  ?.includes(state.inputValue.toLowerCase()),
                              )
                            }
                            sx={{ width: "100%" }}
                            ListboxProps={{
                              style: {
                                maxHeight: "150px",
                              },
                            }}
                            onChange={(event, value) => {
                              if (value) {
                                handleChange(
                                  index,
                                  "eligiblity_type",
                                  value.name,
                                );
                                handleChange(index, "departments", []);
                              }
                            }}
                            renderInput={(params) => (
                              <Mui.TextField
                                {...params}
                                sx={{ background: themes.whiteColor }}
                                placeholder="Select eligibility type"
                                size="small"
                                error={
                                  rowError?.[index]?.eligiblity_type || false
                                }
                                helperText={
                                  rowError[index]?.eligiblity_type
                                    ? row.eligiblity_type.trim() === ""
                                      ? "Select eligibility type"
                                      : ""
                                    : ""
                                }
                              />
                            )}
                          />
                        </Mui.TableCell>

                        <Mui.TableCell sx={{ width: "50%" }}>
                          <Mui.Autocomplete
                            multiple
                            id={`departments-${index}`}
                            size="small"
                            limitTags={1}
                            disableCloseOnSelect
                            options={
                              row?.eligiblity_type === "Department"
                                ? departmentList
                                : row?.eligiblity_type === "Designation"
                                  ? designationList
                                  : row?.eligiblity_type === "Role"
                                    ? roleList
                                    : row?.eligiblity_type === "Individual"
                                      ? individualList
                                      : []
                            }
                            value={row.departments || []}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            ListboxProps={{
                              style: {
                                maxHeight: "200px",
                              },
                            }}
                            onChange={(event, value) => {
                              handleChange(index, "departments", value);
                            }}
                            renderOption={(props, option, { selected }) => {
                              const { key, ...optionProps } = props;
                              return (
                                <li key={key} {...optionProps}>
                                  <Mui.Checkbox
                                    icon={
                                      <Icons.CheckBoxOutlineBlank fontSize="small" />
                                    }
                                    checkedIcon={
                                      <Icons.CheckBox fontSize="small" />
                                    }
                                    checked={selected}
                                  />
                                  {option.name}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <Mui.TextField
                                {...params}
                                multiline
                                rows={1}
                                maxRows={1}
                                error={rowError?.[index]?.departments || false}
                                helperText={
                                  rowError[index]?.departments
                                    ? row.departments?.length === 0
                                      ? row?.eligiblity_type === "Department"
                                        ? "Select the department"
                                        : row?.eligiblity_type === "Designation"
                                          ? "Select the designation"
                                          : row?.eligiblity_type === "Role"
                                            ? "Select the role"
                                            : row?.eligiblity_type ===
                                                "Individual"
                                              ? "Select the user"
                                              : "Select the value"
                                      : ""
                                    : ""
                                }
                                placeholder={
                                  row?.departments?.length === 0
                                    ? row?.eligiblity_type === "Department"
                                      ? "Select the department"
                                      : row?.eligiblity_type === "Designation"
                                        ? "Select the designation"
                                        : row?.eligiblity_type === "Role"
                                          ? "Select the role"
                                          : row?.eligiblity_type ===
                                              "Individual"
                                            ? "Select the user"
                                            : "Select the value"
                                    : ""
                                }
                                InputProps={{
                                  ...params.InputProps,
                                }}
                              />
                            )}
                          />
                        </Mui.TableCell>

                        <Mui.TableCell>
                          <Mui.Tooltip
                            arrow
                            slotProps={tooltipStyleProps}
                            title={"Remove"}
                          >
                            <Mui.IconButton
                              onClick={() => {
                                handleRemoveRow(index);
                              }}
                              disabled={rows?.length === 1}
                            >
                              <Cancel
                                sx={{
                                  color:
                                    rows?.length === 1
                                      ? themes?.disable
                                      : themes?.redColor,
                                }}
                              />
                            </Mui.IconButton>
                          </Mui.Tooltip>
                        </Mui.TableCell>
                      </Mui.TableBody>
                    );
                  })}
                </Mui.Table>
              </Mui.TableContainer>
              <Mui.Grid container>
                <Mui.Grid item xs={10.9}></Mui.Grid>
                <Mui.Grid item>
                  <Mui.Tooltip
                    arrow
                    slotProps={tooltipStyleProps}
                    title={"Add Row"}
                  >
                    <Mui.IconButton onClick={handleAddRow}>
                      <AddCircle sx={{ color: themes.primaryIndicator }} />
                    </Mui.IconButton>
                  </Mui.Tooltip>
                </Mui.Grid>
              </Mui.Grid>
            </Box>
            <Divider />

            <Box>
              <Typography sx={subHeaderStyle}>Qualification</Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 4,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Qualification Condition
                </Typography>
                <Typography {...infoStyle}>
                  Choose rule employees must meet.
                </Typography>
                <CustomAutoComplete
                  dataList={qualificationCondition}
                  selectedValue={watch("qualification_type")}
                  rules={{
                    ...register("qualification_type", {
                      required: "Qualification type is required",
                    }),
                  }}
                  updateDataFunction={handleQualification}
                  placeHolder="Select the qualification type"
                  errorMessage={
                    errors?.qualification_type?.message
                      ? errors?.qualification_type?.message
                      : ""
                  }
                  textBoxError={errors?.qualification_type ? true : false}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Qualification Value
                </Typography>
                <Typography {...infoStyle}>
                  Enter the target value to qualify.
                </Typography>
                {watch("qualification_type") === "Count" ? (
                  <CustomTextBox
                    selectedValue={watch("qualification_value")}
                    rules={{
                      ...register("qualification_value", {
                        required: false,
                        validate: {
                          minLengthNoSpaces: (value) => {
                            if (
                              watch("qualification_type") === "Count" &&
                              (!watch("qualification_value") ||
                                watch("qualification_value") === "0")
                            ) {
                              return "Qualification value is required";
                            } else {
                              return true;
                            }
                          },
                        },
                      }),
                    }}
                    updateDataFunction={handleQualificationCount}
                    placeHolder="Enter the count"
                    errorMessage={
                      errors?.qualification_value?.message
                        ? errors?.qualification_value?.message
                        : ""
                    }
                    textBoxError={errors.qualification_value ? true : false}
                  />
                ) : (
                  <CustomTextBox
                    selectedValue={watch("qualification_value")}
                    rules={{
                      ...register("qualification_value", {
                        required: false,
                        validate: {
                          minLengthNoSpaces: (value) => {
                            if (
                              watch("qualification_type") === "Percentage" &&
                              (!watch("qualification_value") ||
                                watch("qualification_value") === "0")
                            ) {
                              return "qualification percentage is required";
                            } else {
                              return true;
                            }
                          },
                        },
                      }),
                    }}
                    updateDataFunction={handleQualificationPercentage}
                    placeHolder="Enter the percentage"
                    errorMessage={
                      errors?.qualification_value?.message
                        ? errors?.qualification_value?.message
                        : ""
                    }
                    textBoxError={errors.qualification_value ? true : false}
                  />
                )}
              </Box>
            </Box>

            <Divider />
            <Box>
              <Typography sx={subHeaderStyle}>Payout</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Payout Type</Typography>
              <Typography {...infoStyle}>
                Choose how the incentive amount will be calculated.
              </Typography>
              <CustomAutoComplete
                dataList={payoutType}
                selectedValue={watch("payout_type")}
                rules={{
                  ...register("payout_type", {
                    required: "Payout type is required",
                  }),
                }}
                updateDataFunction={handlePayoutType}
                placeHolder="Select the payout type"
                errorMessage={
                  errors?.payout_type?.message
                    ? errors?.payout_type?.message
                    : ""
                }
                textBoxError={errors?.payout_type ? true : false}
              />
            </Box>
            {(watch("payout_type") === "% of salary" || watch("payout_type") === "% of salary") && (
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Measurement Period (Days)</Typography>
                <Typography {...infoStyle}>
                  Enter incentive measurement period.
                </Typography>
                <CustomTextBox
                  selectedValue={watch("salary_divisor")}
                  rules={{
                    ...register("salary_divisor", {
                      required: "Payout divisor is required",
                    }),
                  }}
                  updateDataFunction={handlePayoutDivisor}
                  placeHolder="Payout divisor is required"
                  errorMessage={
                    errors?.salary_divisor?.message
                      ? errors?.salary_divisor?.message
                      : ""
                  }
                  textBoxError={errors.salary_divisor ? true : false}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 4,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Payout Value</Typography>
                <Typography {...infoStyle}>
                  Enter incentive based on payout type.
                </Typography>
                <CustomTextBox
                  selectedValue={watch("payout_value")}
                  rules={{
                    ...register("payout_value", {
                      required: "Payout value is required",
                    }),
                  }}
                  updateDataFunction={handlePayOutValue}
                  placeHolder="Enter the payout value"
                  errorMessage={
                    errors?.payout_value?.message
                      ? errors?.payout_value?.message
                      : ""
                  }
                  textBoxError={errors.payout_value ? true : false}
                />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Payout Basis</Typography>
                <Typography {...infoStyle}>
                  Select when this incentive is applied.
                </Typography>
                <CustomAutoComplete
                  dataList={payoutBasis}
                  selectedValue={watch("payout_basis")}
                  rules={{
                    ...register("payout_basis", {
                      required: "Payout basis is required",
                    }),
                  }}
                  updateDataFunction={hanldePayoutBasis}
                  placeHolder="Select the payout basis"
                  errorMessage={
                    errors?.payout_basis?.message
                      ? errors?.payout_basis?.message
                      : ""
                  }
                  textBoxError={errors?.payout_basis ? true : false}
                />
              </Box>
            </Box>
            <Divider />

            <Box>
              <Typography sx={subHeaderStyle}>Maximum Cap</Typography>
            </Box>
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Maximum Cap</Typography>
                  <Typography {...infoStyle}>
                    Set the maximum amount an employee can receive from this
                    incentive.
                  </Typography>
                </Box>
                <Box sx={{ mr: 1.6 }}>
                  <Mui.Switch
                    {...register("is_maximum_cap_allowed")}
                    checked={watch("is_maximum_cap_allowed")}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setValue("is_maximum_cap_allowed", checked);
                    }}
                  />
                </Box>
              </Box>

              {watch("is_maximum_cap_allowed") && (
                <CustomTextBox
                  selectedValue={watch("maximum_cap")}
                  rules={{
                    ...register("maximum_cap", {
                      required: false,
                      validate: {
                        minLengthNoSpaces: (value) => {
                          if (
                            watch("is_maximum_cap_allowed") &&
                            (!watch("maximum_cap") ||
                              watch("maximum_cap") === "0")
                          ) {
                            return "Maximum cap is required";
                          } else {
                            return true;
                          }
                        },
                      },
                    }),
                  }}
                  updateDataFunction={handleMaximumcap}
                  placeHolder="Enter the maximum cap value"
                  errorMessage={
                    errors?.maximum_cap?.message
                      ? errors?.maximum_cap?.message
                      : ""
                  }
                  textBoxError={errors.maximum_cap ? true : false}
                />
              )}
            </Box>

            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName={isEdit ? "Update" : "Create"}
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
